import React, {
  useState,
  useEffect,
} from "react";
import {
  useNavigate,
  useLocation,
} from "react-router-dom";
import {
  Search,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from "lucide-react";

import {
  Modal,
  message,
  Spin,
} from "antd";

import ReusableHeader from "../../../../reusableField/ReusableHeader";

import DemographicJPFitmentScenario from "./DemographicJPFitmentScenario/DemographicJPFitmentScenario";

import BackIcon from "../../../../Images/backIcon.png";
import HomeIcon from "../../../../Images/homeIcon.png";

import EditIcon from "../../../../Images/editIcon.png";
import DeleteIcon from "../../../../Images/deleteIcon2.png";

import {
  getDemographicQuestions,
  deleteDemographicQuestion,
} from "../../../../services/SyncApis";


const AssessmentConfiguration = () => {

  /* =========================================================
     DELETE STATE
     ========================================================= */

  const [
    deleteModalOpen,
    setDeleteModalOpen,
  ] = useState(false);

  const [
    deleteQuestionId,
    setDeleteQuestionId,
  ] = useState(null);


  /* =========================================================
     QUESTIONS STATE
     ========================================================= */

  const [
    questions,
    setQuestions,
  ] = useState([]);

  const [
    loading,
    setLoading,
  ] = useState(false);


  /* =========================================================
     SEARCH + PAGINATION
     ========================================================= */

  const [
    searchQuery,
    setSearchQuery,
  ] = useState("");

  const [
    currentPage,
    setCurrentPage,
  ] = useState(1);

  const pageSize = 5;


  /* =========================================================
     ROUTER
     ========================================================= */

  const navigate = useNavigate();

  const location = useLocation();

  const fullState =
    location.state || {};


  /* =========================================================
     ROLE / CLIENT DETAILS
     ========================================================= */

  const name =
    location.state?.name;

  const accountName =
    location.state?.accountName;

  const roleId =
    location.state?.roleId;

  const accountId =
    location.state?.accountId;


  /*
   * This page represents the Demographic
   * Assessment tab.
   *
   * Keep this value explicit so that the
   * next AddQuiz page knows which assessment
   * type opened it.
   */
  const assessmentType =
    "demographic";


  /* =========================================================
     GET ALL DEMOGRAPHIC QUESTIONS
     ========================================================= */

  const fetchQuestions = async () => {

    if (!roleId) {
      return;
    }

    if (!accountId) {
      message.error(
        "Admin client ID is missing."
      );

      return;
    }

    setLoading(true);

    try {

      const response =
        await getDemographicQuestions(
          Number(roleId),
          Number(accountId),
          {
            page: 1,
            limit: 100,
          }
        );


      /*
       * Backend response can be:
       *
       * {
       *   success: true,
       *   data: [...]
       * }
       *
       * or directly an array.
       */

      const responseData =
        response?.data;


      if (Array.isArray(responseData)) {

        setQuestions(
          responseData
        );

      } else if (
        Array.isArray(
          responseData?.data
        )
      ) {

        setQuestions(
          responseData.data
        );

      } else if (
        Array.isArray(response)
      ) {

        setQuestions(
          response
        );

      } else {

        setQuestions([]);
      }

    } catch (error) {

      console.error(
        "Error fetching demographic questions:",
        error
      );

      const apiMessage =
        error?.response?.data?.message;

      message.error(
        apiMessage ||
        "Failed to fetch questions."
      );

    } finally {

      setLoading(false);
    }
  };


  /* =========================================================
     LOAD QUESTIONS
     ========================================================= */

  useEffect(() => {

    fetchQuestions();

  }, [
    roleId,
    accountId,
  ]);


  /* =========================================================
     DELETE QUESTION
     ========================================================= */

  const handleDelete = async () => {

    if (
      !deleteQuestionId ||
      !roleId
    ) {
      return;
    }


    if (!accountId) {

      message.error(
        "Admin client ID is missing."
      );

      return;
    }


    try {

      const options = {

        adminClientId:
          Number(accountId),

        /*
         * Do NOT send:
         *
         * changedBy: null
         *
         * because our service intentionally
         * omits it when no real admin ID exists.
         */

      };


      await deleteDemographicQuestion(
        Number(roleId),
        deleteQuestionId,
        options
      );


      message.success(
        "Question deleted successfully."
      );


      setDeleteModalOpen(
        false
      );

      setDeleteQuestionId(
        null
      );


      await fetchQuestions();

    } catch (error) {

      console.error(
        "Error deleting question:",
        error
      );

      const apiMessage =
        error?.response?.data?.message;

      message.error(
        apiMessage ||
        "Failed to delete question."
      );
    }
  };


  /* =========================================================
     SAFE TEXT HELPER
     ========================================================= */

  const getLocalizedText = (
    value,
    lang = "en"
  ) => {

    if (
      value === null ||
      value === undefined
    ) {
      return "";
    }


    if (
      typeof value === "string"
    ) {
      return value;
    }


    if (
      typeof value === "object"
    ) {

      if (
        value[lang] &&
        typeof value[lang] === "string"
      ) {
        return value[lang];
      }


      const firstString =
        Object.values(
          value
        ).find(
          (item) =>
            typeof item === "string"
        );


      if (firstString) {
        return firstString;
      }


      try {

        return JSON.stringify(
          value
        );

      } catch (error) {

        return String(value);
      }
    }


    return String(value);
  };


  /* =========================================================
     SEARCH
     ========================================================= */

  const filteredQuestions =
    questions.filter(
      (question) => {

        const text =
          getLocalizedText(
            question.quiz ||
            question.question ||
            question.title ||
            ""
          );


        return text
          .toLowerCase()
          .includes(
            getLocalizedText(
              searchQuery
            ).toLowerCase()
          );
      }
    );


  /* =========================================================
     PAGINATION
     ========================================================= */

  const totalPages =
    Math.max(
      1,
      Math.ceil(
        filteredQuestions.length /
        pageSize
      )
    );

  const paginatedQuestions =
    filteredQuestions.slice(
      (currentPage - 1) *
      pageSize,

      currentPage *
      pageSize
    );


  /* =========================================================
     RENDER
     ========================================================= */

  return (

    <div className="min-h-screen bg-[#F6F6F6]">

      <ReusableHeader />


      <div className="px-8 pb-8">


        {/* =====================================================
            TOP AREA
            ===================================================== */}

        <div className="mb-4 -mt-8">


          {/* Back + Home */}

          <div className="flex justify-between items-center w-full mt-9 ml-1">


            {/* Back */}

            <button
              onClick={() =>
                navigate(
                  "/sync-configuration",
                  {
                    state:
                      fullState,
                  }
                )
              }

              className="
                flex
                items-center
                gap-2
                bg-[#C21A17]
                text-white
                px-5
                py-2
                rounded-lg
                text-sm
                font-semibold
                shadow-md
                hover:bg-[#A91513]
                transition-all
                duration-200
              "
            >

              <img
                src={BackIcon}
                alt="Back"
                className="w-[15px] h-[15px] object-contain"
              />

              Back

            </button>


            {/* Home */}

            <button
              onClick={() =>
                navigate(
                  "/admin"
                )
              }

              className="
                flex
                items-center
                gap-2
                bg-[#C21A17]
                text-white
                px-5
                py-2
                rounded-lg
                text-sm
                font-semibold
                shadow-md
                hover:bg-[#A91513]
                transition-all
                duration-200
              "
            >

              <img
                src={HomeIcon}
                alt="Home"
                className="w-[15px] h-[15px] object-contain"
              />

              Home

            </button>

          </div>


          {/* =================================================
              BREADCRUMB
              ================================================= */}

          <div className="
            mt-3
            ml-2
            flex
            items-center
            flex-wrap
            text-[14px]
            text-[#7A7A7A]
            gap-1
          ">

            <span
              onClick={() =>
                navigate("/admin")
              }
              className="cursor-pointer"
            >
              {accountName}
            </span>

            <span className="mx-1">
              ›
            </span>

            <span
              onClick={() =>
                navigate("/dashboard", {
                  state: fullState,
                })
              }
              className="cursor-pointer"
            >
              {fullState?.name || ""}
            </span>

            <span className="mx-1">
              ›
            </span>

            <span
              onClick={() =>
                navigate("/experience-configuration", {
                  state: fullState,
                })
              }
              className="cursor-pointer"
            >
              Experience Configuration
            </span>

            <span className="mx-1">
              ›
            </span>

            <span
              onClick={() =>
                navigate("/productsConfiguration", {
                  state: fullState,
                })
              }
              className="cursor-pointer"
            >
              Product Configuration
            </span>

            <span className="mx-1">
              ›
            </span>

            <span
              onClick={() =>
                navigate("/sync-configuration", {
                  state: fullState,
                })
              }
              className="cursor-pointer"
            >
              Sync
            </span>

            <span className="mx-1">
              ›
            </span>

            <span
              className="
      font-semibold
      text-[#1B1B1B]
    "
            >
              Assessment Configuration
            </span>

          </div>

        </div>


        {/* =====================================================
            TABS
            ===================================================== */}

        <DemographicJPFitmentScenario />


        {/* =====================================================
            MAIN CARD
            ===================================================== */}

        <div className="
          mt-8
          bg-white
          rounded-[6px]
          shadow-sm
          p-4
        ">


          <h2 className="
            text-[18px]
            font-semibold
            text-[#1B1B1B]
          ">
            Demographic Assessment
          </h2>


          {/* =================================================
              SEARCH + ADD
              ================================================= */}

          <div className="
            flex
            gap-3
            mt-4
          ">


            {/* Search */}

            <div className="
              flex-1
              relative
            ">

              <input
                type="text"

                placeholder="Search Quiz..."

                value={
                  searchQuery
                }

                onChange={(event) => {

                  setSearchQuery(
                    event.target.value
                  );

                  setCurrentPage(1);

                }}

                className="
                  w-full
                  border
                  rounded-[6px]
                  h-10
                  px-3
                  pr-10
                  text-sm
                  outline-none
                "
              />


              <Search
                size={16}
                className="
                  absolute
                  right-4
                  top-3
                  text-gray-500
                "
              />

            </div>


            {/* Add Quiz */}

            <button
              onClick={() =>
                navigate(
                  "/Add-Quize",
                  {
                    state: {
                      ...fullState,

                      /*
                       * Tell AddQuiz which
                       * assessment it belongs to.
                       */
                      type:
                        assessmentType,
                    },
                  }
                )
              }

              className="
                bg-[#AD0101]
                text-white
                px-4
                h-10
                text-sm
                rounded-[6px]
              "
            >
              Add Quiz
            </button>

          </div>


          {/* =================================================
              QUESTIONS
              ================================================= */}

          {loading ? (

            <div className="
              flex
              justify-center
              items-center
              h-[140px]
            ">
              <Spin size="large" />
            </div>

          ) : paginatedQuestions.length === 0 ? (

            <div className="
              h-[140px]
              flex
              items-center
              justify-center
              text-[#B5B5B5]
              text-sm
            ">
              No Data Available
            </div>

          ) : (

            paginatedQuestions.map(
              (question) => {

                const questionId =
                  question.id ||
                  question._id;


                return (

                  <div
                    key={questionId}

                    className="
                      mt-4
                      border
                      border-[#E7E7E7]
                      rounded-[6px]
                      overflow-hidden
                      bg-white
                      flex
                      justify-between
                      items-center
                    "
                  >


                    {/* LEFT */}

                    <div className="flex">


                      <div className="
                        px-4
                        py-3
                        flex
                        flex-col
                        justify-center
                      ">


                        <span className="
                          w-fit
                          px-3
                          py-1
                          rounded-[6px]
                          bg-[#F3F3F3]
                          text-[#7B7B7B]
                          text-xs
                          font-medium
                        ">
                          Quiz
                        </span>


                        <h3 className="
                          mt-2
                          text-[16px]
                          font-semibold
                          text-[#222]
                        ">

                          {
                            getLocalizedText(
                              question.quiz ||
                              question.question ||
                              question.title
                            ) ||
                            "Untitled"
                          }

                        </h3>


                        <div className="
                          flex
                          items-center
                          gap-1
                          text-sm
                          text-[#7A7A7A]
                        ">

                          <svg
                            width={15}
                            height={15}
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth={1.8}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            aria-hidden="true"
                          >
                            <path d="M2.5 5.5c0-1.9 2.7-3.5 6-3.5s6 1.6 6 3.5-2.7 3.5-6 3.5-6-1.6-6-3.5Z" />
                            <path d="M2.5 5.5v7.2c0 1.6 1.6 2.9 4 3.3" />
                            <path d="M2.5 9.5c0 1.7 2.7 3.1 6 3.1" />
                            <path d="M11.5 13.5c0-1.9 2.4-3.5 5.5-3.5s5.5 1.6 5.5 3.5-2.4 3.5-5.5 3.5-5.5-1.6-5.5-3.5Z" />
                            <path d="M11.5 13.5v5c0 1.9 2.4 3.5 5.5 3.5s5.5-1.6 5.5-3.5v-5" />
                            <path d="M11.5 17c0 1.9 2.4 3.5 5.5 3.5s5.5-1.6 5.5-3.5" />
                          </svg>

                          <span>
                            {
                              question.weightage ||
                              question.points ||
                              0
                            }{" "}
                            points
                          </span>

                        </div>

                      </div>

                    </div>


                    {/* RIGHT */}

                    <div className="
                      flex
                      flex-col
                      items-end
                      justify-between
                      h-full
                      px-3
                      py-3
                    ">


                      <span
                        className={`
                          px-3
                          py-1
                          rounded-[6px]
                          text-xs
                          font-semibold

                          ${question.isActive !==
                            false

                            ? "bg-[#EAF8EA] text-[#1E9B3E]"

                            : "bg-[#FEE2E2] text-[#DC2626]"
                          }
                        `}
                      >

                        {
                          question.isActive !==
                            false
                            ? "Active"
                            : "Inactive"
                        }

                      </span>


                      <div className="
                        flex
                        gap-2
                        mt-3
                      ">


                        {/* EDIT */}

                        <button
                          type="button"
                          aria-label="Edit quiz"
                          onClick={() =>
                            navigate(
                              "/Add-Quize",
                              {
                                state: {
                                  ...fullState,
                                  questionId: questionId,
                                  editMode: true,
                                  type: assessmentType,
                                },
                              }
                            )
                          }
                          style={{
                            width: 32,
                            height: 32,
                            minWidth: 32,
                            minHeight: 32,
                            padding: 0,
                            border: 0,
                            borderRadius: "6px",
                            background: "#F3F4F6",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            cursor: "pointer",
                          }}
                        >
                          <img
                            src={EditIcon}
                            alt="Edit"
                            style={{
                              width: 15,
                              height: 18,
                              objectFit: "contain",
                            }}
                          />
                        </button>


                        {/* DELETE */}

                        <button
                          type="button"

                          onClick={() => {

                            setDeleteQuestionId(
                              questionId
                            );

                            setDeleteModalOpen(
                              true
                            );

                          }}

                          style={{
                            width: 32,
                            height: 32,
                            minWidth: 32,
                            minHeight: 32,
                            padding: 0,
                            border: 0,
                            borderRadius: "6px",
                            background: "#F5F5F5",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            cursor: "pointer",
                          }}
                        >

                          <img
                            src={DeleteIcon}
                            alt="Delete"
                            style={{
                              width: 28,
                              height: 28,
                              objectFit: "contain",
                            }}
                          />

                        </button>

                      </div>

                    </div>

                  </div>

                );
              }
            )

          )}


          {/* =================================================
              DELETE MODAL
              ================================================= */}

          <Modal
            open={
              deleteModalOpen
            }

            footer={null}

            closable={false}

            centered

            width="min(480px, calc(100vw - 32px))"

            styles={{
              content: { padding: 18 },
              body: { padding: 0 },
            }}

            onCancel={() => {

              setDeleteModalOpen(
                false
              );

              setDeleteQuestionId(
                null
              );

            }}
          >

            <div className="
              px-0
              py-0
            ">


              <h2 className="
                text-[18px]
                font-semibold
                text-[#2F2F2F]
              ">
                Are you sure you want to delete this Quiz?
              </h2>


              <p className="
                mt-2
                text-[14px]
                leading-6
                text-[#555]
              ">
                This will permanently delete the quiz and its questions. This action cannot be undone.
              </p>


              <p className="
                mt-2
                text-[14px]
                text-[#444]
              ">
                Are you{" "}
                <span className="font-semibold">
                  Sure?
                </span>
              </p>


              <div className="
                flex
                justify-end
                gap-3
                mt-3
              ">


                <button
                  onClick={() => {

                    setDeleteModalOpen(
                      false
                    );

                    setDeleteQuestionId(
                      null
                    );

                  }}

                  className="
                    w-[96px]
                    h-[38px]
                    border
                    border-gray-400
                    rounded-[6px]
                    text-sm
                    text-gray-600
                    font-medium
                    hover:bg-gray-100
                  "
                >
                  Cancel
                </button>


                <button
                  onClick={
                    handleDelete
                  }

                  className="
                    w-[104px]
                    h-[38px]
                    rounded-[6px]
                    bg-[#C40000]
                    text-white
                    text-sm
                    font-medium
                    hover:bg-[#A50000]
                  "
                >
                  Delete
                </button>

              </div>

            </div>

          </Modal>


          {/* =================================================
              PAGINATION
              ================================================= */}

          <div className="
            flex
            justify-end
            items-center
            gap-2
            mt-3
            border-t
            pt-3
          ">
            <button type="button" aria-label="First page" disabled={currentPage === 1} className="text-gray-700 disabled:text-gray-300" onClick={() => setCurrentPage(1)}><ChevronsLeft size={18} /></button>
            <button type="button" aria-label="Previous page" disabled={currentPage === 1} className="text-gray-700 disabled:text-gray-300" onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}><ChevronLeft size={18} /></button>
            <span aria-current="page" className="flex h-8 min-w-8 items-center justify-center rounded-md border border-gray-300 bg-white px-2 text-sm text-[#C40000]">{currentPage}</span>
            <button type="button" aria-label="Next page" disabled={currentPage >= totalPages} className="text-gray-700 disabled:text-gray-300" onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}><ChevronRight size={18} /></button>
            <button type="button" aria-label="Last page" disabled={currentPage >= totalPages} className="text-gray-700 disabled:text-gray-300" onClick={() => setCurrentPage(totalPages)}><ChevronsRight size={18} /></button>
          </div>

        </div>

      </div>

    </div>
  );
};


export default AssessmentConfiguration;